import {
  PlaytestEventRowSchema,
  PlaytestQuestionRowSchema,
  PlaytestRowSchema,
  type Playtest,
  type PlaytestEvent,
  type PlaytestEventInput,
  type PlaytestEventWithQuestions,
  type PlaytestParticipationRule,
  type PlaytestQuestion,
  type PlaytestQuestionInput,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Playtests data layer.
//
// Draft-time surface for PR 1 of the epic. Reads go through `from()` since
// RLS already restricts rows to the caller's studio; writes go through the
// security-definer RPCs in 0051 so the per-kind config validation lives in
// one place. The Realtime subscription that keeps the list fresh is owned
// by ShellProvider (one channel per active game), not here - this file is
// just thin RPC wrappers + row -> camelCase mapping.
// ---------------------------------------------------------------------------

const PLAYTESTS_TABLE = 'playtests';
const EVENTS_TABLE = 'playtest_events';
const QUESTIONS_TABLE = 'playtest_questions';

function parsePlaytest(raw: unknown): Playtest | null {
  const parsed = PlaytestRowSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn('[playtests] Could not parse playtest row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

function parseEvent(raw: unknown): PlaytestEvent | null {
  const parsed = PlaytestEventRowSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn('[playtests] Could not parse event row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

function parseQuestion(raw: unknown): PlaytestQuestion | null {
  const parsed = PlaytestQuestionRowSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn('[playtests] Could not parse question row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

export async function fetchPlaytestsForGame(
  gameId: string,
): Promise<Playtest[]> {
  const { data, error } = await supabase
    .from(PLAYTESTS_TABLE)
    .select('*')
    .eq('game_id', gameId)
    .order('created_at', { ascending: false });

  if (error !== null) {
    console.warn('[playtests] Failed to load playtests:', error.message);
    return [];
  }
  const out: Playtest[] = [];
  for (const raw of data ?? []) {
    const parsed = parsePlaytest(raw);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

export async function fetchPlaytest(
  playtestId: string,
): Promise<Playtest | null> {
  const { data, error } = await supabase
    .from(PLAYTESTS_TABLE)
    .select('*')
    .eq('id', playtestId)
    .maybeSingle();

  if (error !== null) {
    console.warn('[playtests] Failed to load playtest:', error.message);
    return null;
  }
  if (data === null) return null;
  return parsePlaytest(data);
}

// Load the whole tree (events + their questions) in one round trip. The
// builder uses this on first mount and after realtime ticks; the wire
// payload is small enough that we don't paginate inside a playtest.
export async function fetchPlaytestTree(
  playtestId: string,
): Promise<PlaytestEventWithQuestions[]> {
  const { data: eventRows, error: eventsErr } = await supabase
    .from(EVENTS_TABLE)
    .select('*')
    .eq('playtest_id', playtestId)
    .order('order_index', { ascending: true });
  if (eventsErr !== null) {
    console.warn('[playtests] Failed to load events:', eventsErr.message);
    return [];
  }

  const events: PlaytestEvent[] = [];
  for (const raw of eventRows ?? []) {
    const parsed = parseEvent(raw);
    if (parsed !== null) events.push(parsed);
  }
  if (events.length === 0) return [];

  const eventIds = events.map((e) => e.id);
  const { data: questionRows, error: qErr } = await supabase
    .from(QUESTIONS_TABLE)
    .select('*')
    .in('event_id', eventIds)
    .order('order_index', { ascending: true });
  if (qErr !== null) {
    console.warn('[playtests] Failed to load questions:', qErr.message);
    return events.map((event) => ({ ...event, questions: [] }));
  }

  const byEventId = new Map<string, PlaytestQuestion[]>();
  for (const raw of questionRows ?? []) {
    const parsed = parseQuestion(raw);
    if (parsed === null) continue;
    const list = byEventId.get(parsed.eventId);
    if (list === undefined) {
      byEventId.set(parsed.eventId, [parsed]);
    } else {
      list.push(parsed);
    }
  }

  return events.map((event) => ({
    ...event,
    questions: byEventId.get(event.id) ?? [],
  }));
}

// ---------------------------------------------------------------------------
// RPC wrappers.
//
// Error tokens mirror the raise exception strings in 0051. The portal maps
// each token to user-facing copy at the call site so phrasing stays close
// to the surface.
// ---------------------------------------------------------------------------

export type PlaytestRpcError =
  | 'not_authenticated'
  | 'not_a_member'
  | 'game_not_found'
  | 'playtest_not_found'
  | 'event_not_found'
  | 'name_required'
  | 'name_too_long'
  | 'playtest_active_cannot_edit'
  | 'playtest_active_cannot_delete'
  | 'playtest_not_active'
  | 'playtest_has_sessions'
  | 'events_must_be_array'
  | 'too_many_events'
  | 'event_name_required'
  | 'event_name_too_long'
  | 'event_name_invalid'
  | 'event_label_required'
  | 'event_label_too_long'
  | 'event_headline_too_long'
  | 'event_button_label_too_long'
  | 'event_questions_must_be_array'
  | 'event_too_many_questions'
  | 'questions_must_be_array'
  | 'invalid_question_kind'
  | 'question_prompt_required'
  | 'question_prompt_too_long'
  | 'question_help_text_too_long'
  | 'question_config_must_be_object'
  // Feedback-form-aligned kinds (mirror migration 0029 / 0051 validators).
  | 'rating_display_must_be_object'
  | 'rating_display_mode_invalid'
  | 'rating_display_emoji_invalid'
  | 'select_options_required'
  | 'select_needs_at_least_one_option'
  | 'select_too_many_options'
  | 'select_max_values_invalid'
  | 'binary_label_must_be_string'
  | 'binary_label_too_long'
  | 'participation_rule_must_be_object'
  | 'participation_rule_kind_required'
  | 'participation_rule_kind_invalid'
  | 'participation_rule_channels_required'
  | 'participation_rule_channels_invalid'
  | 'participation_rule_roles_required'
  | 'participation_rule_roles_invalid'
  | 'playtest_archived'
  | 'playtest_needs_events'
  | 'playtest_event_needs_questions'
  | 'unknown';

export interface PlaytestRpcResult<T> {
  ok: boolean;
  data: T | null;
  error: PlaytestRpcError | null;
}

const KNOWN_ERROR_TOKENS = new Set<PlaytestRpcError>([
  'not_authenticated',
  'not_a_member',
  'game_not_found',
  'playtest_not_found',
  'event_not_found',
  'name_required',
  'name_too_long',
  'playtest_active_cannot_edit',
  'playtest_active_cannot_delete',
  'playtest_not_active',
  'playtest_has_sessions',
  'events_must_be_array',
  'too_many_events',
  'event_name_required',
  'event_name_too_long',
  'event_name_invalid',
  'event_label_required',
  'event_label_too_long',
  'event_headline_too_long',
  'event_button_label_too_long',
  'event_questions_must_be_array',
  'event_too_many_questions',
  'questions_must_be_array',
  'invalid_question_kind',
  'question_prompt_required',
  'question_prompt_too_long',
  'question_help_text_too_long',
  'question_config_must_be_object',
  'rating_display_must_be_object',
  'rating_display_mode_invalid',
  'rating_display_emoji_invalid',
  'select_options_required',
  'select_needs_at_least_one_option',
  'select_too_many_options',
  'select_max_values_invalid',
  'binary_label_must_be_string',
  'binary_label_too_long',
  'participation_rule_must_be_object',
  'participation_rule_kind_required',
  'participation_rule_kind_invalid',
  'participation_rule_channels_required',
  'participation_rule_channels_invalid',
  'participation_rule_roles_required',
  'participation_rule_roles_invalid',
  'playtest_archived',
  'playtest_needs_events',
  'playtest_event_needs_questions',
]);

function classifyError(message: string | undefined | null): PlaytestRpcError {
  if (typeof message !== 'string') return 'unknown';
  for (const token of KNOWN_ERROR_TOKENS) {
    if (message.includes(token)) return token;
  }
  return 'unknown';
}

export async function createPlaytest(
  gameId: string,
  name: string,
): Promise<PlaytestRpcResult<Playtest>> {
  const { data, error } = await supabase.rpc('create_playtest', {
    p_game_id: gameId,
    p_name: name,
  });
  if (error !== null) {
    console.warn('[playtests] create failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parsePlaytest(data);
  if (parsed === null) {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: parsed, error: null };
}

export async function updatePlaytest(
  playtestId: string,
  patch: { name: string; internalDescription: string | null },
): Promise<PlaytestRpcResult<Playtest>> {
  const { data, error } = await supabase.rpc('update_playtest', {
    p_playtest_id: playtestId,
    p_name: patch.name,
    p_internal_description: patch.internalDescription,
  });
  if (error !== null) {
    console.warn('[playtests] update failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parsePlaytest(data);
  if (parsed === null) return { ok: false, data: null, error: 'unknown' };
  return { ok: true, data: parsed, error: null };
}

export async function deletePlaytest(
  playtestId: string,
): Promise<PlaytestRpcResult<null>> {
  const { error } = await supabase.rpc('delete_playtest', {
    p_playtest_id: playtestId,
  });
  if (error !== null) {
    console.warn('[playtests] delete failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  return { ok: true, data: null, error: null };
}

export async function archivePlaytest(
  playtestId: string,
): Promise<PlaytestRpcResult<Playtest>> {
  const { data, error } = await supabase.rpc('archive_playtest', {
    p_playtest_id: playtestId,
  });
  if (error !== null) {
    console.warn('[playtests] archive failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parsePlaytest(data);
  if (parsed === null) return { ok: false, data: null, error: 'unknown' };
  return { ok: true, data: parsed, error: null };
}

export async function disablePlaytest(
  playtestId: string,
): Promise<PlaytestRpcResult<Playtest>> {
  const { data, error } = await supabase.rpc('disable_playtest', {
    p_playtest_id: playtestId,
  });
  if (error !== null) {
    console.warn('[playtests] disable failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parsePlaytest(data);
  if (parsed === null) return { ok: false, data: null, error: 'unknown' };
  return { ok: true, data: parsed, error: null };
}

export async function activatePlaytest(
  playtestId: string,
): Promise<PlaytestRpcResult<Playtest>> {
  const { data, error } = await supabase.rpc('activate_playtest', {
    p_playtest_id: playtestId,
  });
  if (error !== null) {
    console.warn('[playtests] activate failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parsePlaytest(data);
  if (parsed === null) return { ok: false, data: null, error: 'unknown' };
  return { ok: true, data: parsed, error: null };
}

export async function unarchivePlaytest(
  playtestId: string,
): Promise<PlaytestRpcResult<Playtest>> {
  const { data, error } = await supabase.rpc('unarchive_playtest', {
    p_playtest_id: playtestId,
  });
  if (error !== null) {
    console.warn('[playtests] unarchive failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parsePlaytest(data);
  if (parsed === null) return { ok: false, data: null, error: 'unknown' };
  return { ok: true, data: parsed, error: null };
}

export async function setPlaytestParticipationRule(
  playtestId: string,
  rule: PlaytestParticipationRule,
): Promise<PlaytestRpcResult<Playtest>> {
  const { data, error } = await supabase.rpc(
    'set_playtest_participation_rule',
    {
      p_playtest_id: playtestId,
      p_rule: rule,
    },
  );
  if (error !== null) {
    console.warn('[playtests] participation rule update failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parsePlaytest(data);
  if (parsed === null) return { ok: false, data: null, error: 'unknown' };
  return { ok: true, data: parsed, error: null };
}

// Wire payload for replace_playtest_events. PlaytestQuestionInput is
// already shaped for the RPC (kind + prompt + helpText + required +
// config jsonb mirrors feedback_form_fields); we just normalise nulls
// and pass through.
function questionToWire(q: PlaytestQuestionInput): Record<string, unknown> {
  return {
    kind: q.kind,
    prompt: q.prompt,
    helpText: q.helpText ?? null,
    required: q.required ?? true,
    config: q.config ?? {},
  };
}

export async function replacePlaytestEvents(
  playtestId: string,
  events: PlaytestEventInput[],
): Promise<PlaytestRpcResult<PlaytestEvent[]>> {
  const wire = events.map((event) => ({
    name: event.name,
    label: event.label,
    description: event.description ?? null,
    headline: event.headline ?? '',
    buttonLabel: event.buttonLabel ?? 'Share feedback',
    questions: (event.questions ?? []).map(questionToWire),
  }));

  const { data, error } = await supabase.rpc('replace_playtest_events', {
    p_playtest_id: playtestId,
    p_events: wire,
  });

  if (error !== null) {
    // Dump everything postgrest hands back. Pinning the full error object
    // is the only way to tell apart "validation rejected the payload" from
    // "the migration has drifted from the RPC contract"; the bare message
    // strips the SQLSTATE code postgrest carries on `code` / `details` /
    // `hint`.
    console.warn('[playtests] replace events failed:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
      wire,
    });
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const out: PlaytestEvent[] = [];
  for (const raw of (data ?? []) as unknown[]) {
    const parsed = parseEvent(raw);
    if (parsed !== null) out.push(parsed);
  }
  return { ok: true, data: out, error: null };
}

export async function replaceEventQuestions(
  eventId: string,
  questions: NonNullable<PlaytestEventInput['questions']>,
): Promise<PlaytestRpcResult<PlaytestQuestion[]>> {
  const wire = questions.map(questionToWire);
  const { data, error } = await supabase.rpc('replace_event_questions', {
    p_event_id: eventId,
    p_questions: wire,
  });
  if (error !== null) {
    console.warn('[playtests] replace questions failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const out: PlaytestQuestion[] = [];
  for (const raw of (data ?? []) as unknown[]) {
    const parsed = parseQuestion(raw);
    if (parsed !== null) out.push(parsed);
  }
  return { ok: true, data: out, error: null };
}
